import { useState } from 'react';
import DataTable from '../../components/DataTable';
import Pagination from '../../components/Pagination';
import SearchBar from '../../components/SearchBar';
import ConfirmDialog from '../../components/ConfirmDialog';
import Modal from '../../components/Modal';
import { useToast } from '../../context/ToastContext';
import { useFeedbackList, useDeleteFeedback } from '../../hooks/useFeedback';

const LIMIT = 20;

// Reporter = nested user object, else top-level guest fields.
function reporter(row) {
  const u = row.user;
  if (u && (u.name || u.email)) return { name: u.name, email: u.email, phone: u.phone };
  return { name: row.name, email: row.email, phone: row.phone };
}

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d) ? s : d.toLocaleString();
}

// The itinerary.response_json is a big JSON string; pull a readable summary if present.
function itinerarySummary(itinerary) {
  if (!itinerary || !itinerary.response_json) return null;
  try {
    const parsed = JSON.parse(itinerary.response_json);
    const det = parsed?.data?.detailed_itinerary;
    if (!det) return null;
    return { name: det.name, city: det.city, state: det.state, days: det.total_days, description: det.description };
  } catch {
    return null;
  }
}

export default function FeedbackPage() {
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const { data, isLoading, error } = useFeedbackList({ page, limit: LIMIT, search });
  const rows = data?.data || [];

  const delM = useDeleteFeedback({
    onSuccess: () => { toast.success('Deleted'); setDeleting(null); },
    onError: (e) => toast.error(e.message || 'Delete failed'),
  });

  const columns = [
    {
      key: 'reporter', header: 'From', render: (r) => {
        const p = reporter(r);
        return p.name || p.email || <span className="muted">Anonymous</span>;
      },
    },
    { key: 'type', header: 'Type', render: (r) => <span className={`badge badge--${(r.type || '').toLowerCase()}`}>{r.type || '—'}</span> },
    { key: 'message', header: 'Message', render: (r) => <span title={r.message}>{(r.message || '').replace(/\s+/g, ' ').slice(0, 70) || '—'}</span> },
    { key: 'itinerary_id', header: 'Itinerary', render: (r) => r.itinerary_id ?? '—' },
    { key: 'created_at', header: 'Date', render: (r) => fmtDate(r.created_at) },
  ];

  const p = viewing ? reporter(viewing) : null;
  const itin = viewing ? itinerarySummary(viewing.itinerary) : null;

  return (
    <div className="page">
      <div className="page__header"><h1>Feedback</h1></div>
      <div className="page__toolbar">
        <SearchBar value={search} onSearch={(v) => { setSearch(v); setPage(1); }} placeholder="Search feedback…" />
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        isLoading={isLoading}
        error={error}
        emptyText="No feedback yet."
        actions={(row) => (
          <>
            <button className="btn btn--sm" onClick={() => setViewing(row)}>View</button>
            <button className="btn btn--sm btn--danger" onClick={() => setDeleting(row)}>Delete</button>
          </>
        )}
      />

      {data && <Pagination page={page} limit={LIMIT} total={data.total} onPage={setPage} />}

      <Modal open={!!viewing} title="Feedback" onClose={() => setViewing(null)}>
        {viewing && (
          <div className="detail">
            <p><strong>From:</strong> {p.name || 'Anonymous'} {p.email && `· ${p.email}`} {p.phone && `· ${p.phone}`}</p>
            <p><strong>Type:</strong> {viewing.type || '—'}</p>
            <p><strong>Device:</strong> {viewing.device_id || '—'}</p>
            <p><strong>Date:</strong> {fmtDate(viewing.created_at)}</p>
            {viewing.itinerary_id && <p><strong>Itinerary ID:</strong> {viewing.itinerary_id}</p>}
            {itin && (
              <div className="feedback__itinerary">
                <strong>Itinerary:</strong> {itin.name} — {itin.city}, {itin.state} · {itin.days} days
                {itin.description && <p className="muted">{itin.description}</p>}
              </div>
            )}
            <p><strong>Message:</strong></p>
            <pre className="feedback__message">{viewing.message || '—'}</pre>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        message="Delete this feedback? This cannot be undone."
        pending={delM.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => delM.mutate(deleting.id)}
      />
    </div>
  );
}
